import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bluetooth, Copy, Check, RefreshCw, CircleCheck } from "lucide-react";
import { hasPersistentPermissions } from "@/lib/btPrinter";

const PASOS = [
  {
    url: "chrome://flags/#enable-web-bluetooth-new-permissions-backend",
    titulo: "Copia esta dirección y pégala en Chrome",
    detalle: "Abre una pestaña nueva de Chrome, pega la dirección y toca Ir. Si no aparece ninguna opción, usa la de más abajo.",
  },
  {
    url: null,
    titulo: "Cambia «Default» por «Enabled»",
    detalle: "La opción queda resaltada en amarillo. Toca el selector que dice Default y elige Enabled.",
  },
  {
    url: null,
    titulo: "Toca «Relaunch» abajo a la derecha",
    detalle: "Chrome se reinicia solo. Es normal que se cierren las pestañas abiertas.",
  },
  {
    url: null,
    titulo: "Vuelve a Ai-POS y conecta la impresora una vez",
    detalle: "Desde ahí queda recordada: al abrir la caja se conecta sola, sin preguntar nunca más.",
  },
];

const ALTERNATIVA = "chrome://flags/#enable-experimental-web-platform-features";

/** Guía de una sola vez para que Chrome en Android recuerde la impresora Bluetooth.
 *  Chrome no permite abrir enlaces chrome:// desde una página: se copia y se pega. */
export const BtPermisoGuia = ({ open, onOpenChange }) => {
  const [copiado, setCopiado] = useState(null);
  const [listo, setListo] = useState(() => hasPersistentPermissions());

  const copiar = async (texto, clave) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(clave);
      toast.success("Dirección copiada: pégala en una pestaña nueva de Chrome");
      setTimeout(() => setCopiado(null), 2500);
    } catch (_) {
      toast.error("No se pudo copiar. Escríbela tal cual en Chrome.");
    }
  };

  const comprobar = () => {
    const ok = hasPersistentPermissions();
    setListo(ok);
    if (ok) toast.success("¡Listo! Esta tablet ya recuerda la impresora");
    else toast.error("Todavía no queda activado. Revisa que hayas tocado «Relaunch».");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bt-permiso-guia" className="rounded-3xl max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Bluetooth className="w-5 h-5 text-indigo-500" /> Dejar la impresora recordada
          </DialogTitle>
        </DialogHeader>

        {listo ? (
          <div data-testid="bt-permiso-ok" className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
            <CircleCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-emerald-900">Esta tablet ya recuerda la impresora</p>
              <p className="text-xs text-emerald-800 mt-1">
                Conéctala una vez desde la caja y después se reconecta sola cada vez que abras el punto de venta.
              </p>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              Chrome pregunta por la impresora cada vez que abres la caja hasta que actives una opción del navegador.
              Se hace <strong>una sola vez por tablet</strong> y toma menos de un minuto.
            </p>
            <ol className="space-y-3 mt-1">
              {PASOS.map((p, i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center text-sm font-extrabold">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900">{p.titulo}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.detalle}</p>
                    {p.url && (
                      <button
                        type="button"
                        data-testid="bt-permiso-copiar-url"
                        onClick={() => copiar(p.url, "principal")}
                        className="mt-2 w-full flex items-center gap-2 rounded-xl border-2 border-indigo-200 bg-indigo-50 px-3 py-2 text-left active:scale-[0.99]"
                      >
                        {copiado === "principal" ? <Check className="w-4 h-4 text-emerald-600 shrink-0" /> : <Copy className="w-4 h-4 text-indigo-600 shrink-0" />}
                        <code className="text-[11px] font-mono text-indigo-900 break-all">{p.url}</code>
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs font-bold text-slate-700">¿No aparece esa opción en tu Chrome?</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Usa esta otra dirección y déjala en «Enabled» igual que antes.</p>
              <button
                type="button"
                data-testid="bt-permiso-copiar-alt"
                onClick={() => copiar(ALTERNATIVA, "alt")}
                className="mt-2 w-full flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-left active:scale-[0.99]"
              >
                {copiado === "alt" ? <Check className="w-4 h-4 text-emerald-600 shrink-0" /> : <Copy className="w-4 h-4 text-slate-500 shrink-0" />}
                <code className="text-[11px] font-mono text-slate-700 break-all">{ALTERNATIVA}</code>
              </button>
            </div>
          </>
        )}

        <div className="flex gap-2 pt-1">
          {!listo && (
            <Button data-testid="bt-permiso-comprobar" onClick={comprobar}
                    className="flex-1 h-12 rounded-2xl bg-indigo-600 hover:bg-indigo-700 font-bold">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Ya lo activé · Comprobar
            </Button>
          )}
          <Button data-testid="bt-permiso-cerrar" variant="outline" onClick={() => onOpenChange(false)}
                  className={`h-12 rounded-2xl border-2 font-bold ${listo ? "flex-1" : ""}`}>
            {listo ? "Entendido" : "Ahora no"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BtPermisoGuia;
